import { z } from 'zod';

/**
 * Contratto delle rotte di autenticazione amministratore.
 *
 * Schemi e tipi vivono qui perché sono l'unica definizione condivisa fra API e
 * client: il browser valida con gli stessi schemi con cui valida il server, ma
 * la validazione **autorevole** resta quella del server.
 */
export const AUTH_ROUTES = {
  register: '/auth/register',
  login: '/auth/login',
  logout: '/auth/logout',
  me: '/auth/me',
} as const;

/**
 * Politica delle password.
 *
 * Lunghezza minima generosa e nessun obbligo di simboli: è la raccomandazione
 * NIST attuale, e una passphrase lunga batte "P@ssw0rd!".
 */
export const MIN_PASSWORD_LENGTH = 12;
export const MAX_PASSWORD_LENGTH = 200;
export const MAX_EMAIL_LENGTH = 254;

/**
 * Normalizza **prima** di validare: `z.email()` rifiuterebbe un valore con
 * spazi in testa o in coda, che per un utente è la stessa email.
 */
export const emailSchema = z
  .string()
  .trim()
  .transform((email) => email.toLowerCase())
  .pipe(z.email('Email non valida').min(3).max(MAX_EMAIL_LENGTH));

export const passwordSchema = z
  .string()
  .min(MIN_PASSWORD_LENGTH, `La password deve avere almeno ${MIN_PASSWORD_LENGTH} caratteri`)
  .max(MAX_PASSWORD_LENGTH, `La password non può superare ${MAX_PASSWORD_LENGTH} caratteri`);

export const registerRequestSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  displayName: z.string().trim().min(2, 'Nome troppo corto').max(80),
  /**
   * Accettazione di Termini e Informativa privacy: obbligatoria, e per questo un
   * `literal(true)` e non un booleano. Un `false` è un rifiuto, non una
   * preferenza, e non deve poter creare un account.
   *
   * La **versione** accettata non viaggia nella richiesta: la stampa il server
   * con la propria costante. Un client vecchio in cache manderebbe una versione
   * vecchia, e l'unica cosa peggiore di un consenso assente è un consenso che
   * dichiara il documento sbagliato.
   */
  acceptedTerms: z.literal(true, 'Devi accettare i termini di servizio e l’informativa privacy'),
});

export type RegisterRequest = z.input<typeof registerRequestSchema>;

export const loginRequestSchema = z.object({
  email: emailSchema,
  /**
   * In login non si applica la politica di lunghezza: rifiutare una password
   * corta prima di verificarla direbbe all'attaccante che non è quella giusta.
   */
  password: z.string().min(1).max(MAX_PASSWORD_LENGTH),
});

export type LoginRequest = z.input<typeof loginRequestSchema>;

/** Profilo pubblico dell'amministratore. Non contiene mai l'hash della password. */
export interface AdminProfile {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  /** ISO-8601. */
  readonly createdAt: string;
}

export interface AuthenticatedResponse {
  readonly admin: AdminProfile;
  /** Scadenza della sessione, ISO-8601: il client può mostrare un avviso. */
  readonly expiresAt: string;
}

export interface LogoutResponse {
  readonly loggedOut: true;
}
